import { 
    useEffect, 
    useRef,
} from "react";
import {
    useTheme, 
    useMediaQuery,
    Button,
    Box,
  } from '@mui/material';
import ReactGA from 'react-ga4';
import { useDispatch, useSelector } from "react-redux";
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent";
import {
    __DEV__,
    GA4_MEASUREMENT_ID,
    ENABLE_GA4,
} from '../../config';
import { setIsCookieConsentOpen } from "../../slices/isCookieConsentOpenSlice";

const initGA4 = () => {
    if (GA4_MEASUREMENT_ID){
        if (ENABLE_GA4) {
            if (__DEV__) {
                ReactGA.initialize(GA4_MEASUREMENT_ID, {
                    gtagOptions: {
                        debug_mode: true,
                    }
                });
            }
            else {
                ReactGA.initialize(GA4_MEASUREMENT_ID);
            }
        }
    }
    else {
        console.error('GA4 is not enabled!');
    }
}

const CookieBanner = () => {

    const theme = useTheme();
    const dispatch = useDispatch();
    const cookieConsentRef = useRef();
    const matchesDown900 = useMediaQuery(theme.breakpoints.down(900));
    const matchesDown690 = useMediaQuery(theme.breakpoints.down(690));
    const isCookieConsentOpen = useSelector(state => state.isCookieConsentOpen.value);

    useEffect(() => {
        const isConsent = getCookieConsentValue();
        if (isConsent === "true") {
            acceptCookie();
        }
    }, []);

    const acceptCookie = () => {
        window[`ga-disable-${GA4_MEASUREMENT_ID}`] = false;
        initGA4();
    }

    const handleAcceptCookie = () => {
        cookieConsentRef.current.accept();
        acceptCookie();
        dispatch(setIsCookieConsentOpen(false));
    }

    const handleDeclineCookie = () => {
        cookieConsentRef.current.decline();
        stopGA4Tracking();
        dispatch(setIsCookieConsentOpen(false));
    };

    const stopGA4Tracking = () => {        
        // Clear GA cookies if set
        document.cookie = "_ga=; Max-Age=0; path=/";
        document.cookie = "_gid=; Max-Age=0; path=/";
        document.cookie = "_gat=; Max-Age=0; path=/";
        
        // Disable gtag
        window[`ga-disable-${GA4_MEASUREMENT_ID}`] = true;
        
        // Remove GA script
        const gaScript = document.querySelector('script[src*="googletagmanager"]');
        if (gaScript) gaScript.remove();
    }

    const cookieBanner = (
        <CookieConsent 
            ref={cookieConsentRef}
            visible={isCookieConsentOpen}
            enableDeclineButton={false}
            containerClasses="custom-cookie-banner"
            buttonText=""
            style={{
                width: matchesDown690 ? '100%' : 'fit-content',
                maxWidth: '90%',
                color: theme.palette.text.main,
                backgroundColor: theme.palette.mode === 'dark' ? '#222831' : theme.palette.background.default,
                border: theme.palette.mode === 'dark' ? "1px solid #1e2229" : theme.palette.tinyBorder,
                borderRadius: '20px',
            }}
        >
            <Box sx={{
                display: 'flex', 
                flexDirection: matchesDown900 ? 'column' : 'row', 
                alignItems: 'center', 
                gap: matchesDown900 ? 2 : 3,
            }}>
                This website uses cookies to enhance the user experience.
                <Box sx={{ ml: !matchesDown900 && 'auto', display: 'flex', gap: 1}}>
                    <Button variant="outlined" onClick={handleDeclineCookie} sx={{textTransform: "none", fontWeight: 'bold', borderRadius: '30px'}} size='medium'>Reject</Button>
                    <Button variant="contained" onClick={handleAcceptCookie} sx={{textTransform: "none", fontWeight: 'bold', borderRadius: '30px'}} size='medium'>Accept</Button>
                </Box>
            </Box>
        </CookieConsent>
    );

    return isCookieConsentOpen ? cookieBanner : null;
}

export default CookieBanner;